/**
 * ---------------------------------------------------------------------------------------------------------------------
 * REQUIRE AN EXPLICIT VARIANT SELECTION
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Customers were ordering the wrong finish/fabric because Liquid pre-selects the first option value and Add to cart is
 * live immediately. This keeps every option visually unchosen until the shopper actually picks it, and holds Add to cart
 * disabled ("Select options") until all of them are.
 *
 * Why this is a client-side layer rather than Liquid:
 *
 *   Variant selection in this theme is resolved on the server. Every option click refetches
 *   `/products/x?option_values=...&section_id=...` and swaps the returned variant-picker and buy-buttons blocks into the
 *   DOM (see VariantPicker.selectCombination / ProductRerender in theme.js). Shopify renders EVERY option as selected in
 *   that response, even when the request only names one of them - verified against a two-option product. So there is no
 *   server-side representation of "the shopper has not chosen yet"; it only exists here, and has to be re-applied after
 *   each re-render.
 *
 * Two consequences worth knowing before editing:
 *
 *   1. The radio inputs stay `checked`. VariantPicker builds its fetch from `input[data-option-position]:checked`, so
 *      unchecking them would break variant resolution. Only the *appearance* is suppressed.
 *   2. Because the default option stays checked, clicking it fires no `change` event. Selection is therefore confirmed
 *      on `click` as well, otherwise a shopper who picks the value that happens to be the hidden default would be stuck
 *      with a disabled button.
 *
 * State is keyed by form id (stable across re-renders) and dropped when a picker leaves the DOM, so a quick buy modal
 * starts clean each time it is opened.
 */
(function () {
  'use strict';

  if (window.__variantSelectionRequired) return;
  window.__variantSelectionRequired = true;

  var PENDING_ATTR = 'data-selection-pending';
  var PICKER_SELECTOR = 'variant-picker[require-selection]';
  var OPTION_SELECTOR = '.variant-picker__option[data-option-position]';

  /**
   * Read once, on purpose. VariantPicker rewrites the URL with ?variant= after the first change, so re-reading this
   * later would make every product look like it was opened through a variant-specific link.
   */
  var HONOUR_DEEP_LINK = new URLSearchParams(window.location.search).has('variant');

  var touched = new Map(); // form id -> Set of option positions the shopper has confirmed
  var applying = false;

  function strings() {
    var themeStrings = (window.themeVariables && window.themeVariables.strings) || {};

    return {
      pleaseSelect: themeStrings.pleaseSelect || 'Please select',
      selectOptions: themeStrings.selectOptions || 'Select options'
    };
  }

  function touchedSet(formId) {
    var set = touched.get(formId);

    if (!set) {
      set = new Set();
      touched.set(formId, set);
    }

    return set;
  }

  function pickers() {
    return Array.prototype.slice.call(document.querySelectorAll(PICKER_SELECTOR));
  }

  function pickerFor(formId) {
    return document.querySelector(PICKER_SELECTOR + '[form-id="' + formId + '"]');
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Painting
  // ---------------------------------------------------------------------------------------------------------------

  function setValueText(element, value, isPending) {
    if (element.textContent !== value) {
      element.textContent = value;
    }

    element.classList.toggle('is-selection-pending', isPending);
  }

  /**
   * Swatch and dropdown options carry the value in their own markup, so it is replaced in place. Block options have
   * nothing in the legend to replace - the chosen value is the button itself - so the prompt is appended instead and
   * removed again once the option is chosen.
   */
  function togglePlaceholder(fieldset, isPending, label) {
    var legend = fieldset.querySelector('legend');
    if (!legend) return;

    var placeholder = legend.querySelector('[data-selection-placeholder]');

    if (isPending) {
      if (!placeholder) {
        placeholder = document.createElement('span');
        placeholder.setAttribute('data-selection-placeholder', '');
        placeholder.className = 'nowrap bold is-selection-pending';
        legend.appendChild(placeholder);
      }

      if (placeholder.textContent !== label) {
        placeholder.textContent = label;
      }
    } else if (placeholder) {
      placeholder.remove();
    }
  }

  function valueElementFor(fieldset) {
    return (
      fieldset.querySelector('legend [data-selected-value]') ||
      fieldset.querySelector('button.select [data-selected-value]')
    );
  }

  function paintOption(fieldset, isPending) {
    var text = strings();

    if (isPending && !fieldset.hasAttribute(PENDING_ATTR)) {
      fieldset.setAttribute(PENDING_ATTR, '');
    } else if (!isPending && fieldset.hasAttribute(PENDING_ATTR)) {
      fieldset.removeAttribute(PENDING_ATTR);
    }

    var valueElement = valueElementFor(fieldset);

    if (valueElement) {
      setValueText(
        valueElement,
        isPending ? text.pleaseSelect : valueElement.getAttribute('data-selected-value') || '',
        isPending
      );
    } else {
      togglePlaceholder(fieldset, isPending, text.pleaseSelect);
    }
  }

  /**
   * What the button would say if nothing were being held back. buy-buttons carries this because the server may have
   * rendered the button already gated, in which case its own text is "Select options" and useless for restoring.
   */
  function ungatedState(button) {
    var container = button.closest('buy-buttons');
    var label = container && container.getAttribute('data-atc-label');
    var disabled = container && container.getAttribute('data-atc-disabled');

    return {
      label: label || button.textContent.trim(),
      disabled: disabled ? disabled === 'true' : button.disabled
    };
  }

  function gateButton(button, isPending, label) {
    if (isPending) {
      if (button.dataset.selectionGated === 'true') return;

      // Only swap a bare text label; a button rendered with an icon would lose it.
      if (button.firstElementChild) return;

      var ungated = ungatedState(button);

      button.dataset.selectionGated = 'true';
      button.dataset.selectionLabel = ungated.label;
      // "Sold out" belongs to the pre-selected variant, which the shopper has not chosen - so it is held back with
      // everything else and restored if they do land on that combination.
      button.dataset.selectionWasDisabled = ungated.disabled ? 'true' : 'false';

      if (button.textContent.trim() !== label) button.textContent = label;
      button.disabled = true;
      button.classList.add('button--selection-pending');
    } else if (button.dataset.selectionGated === 'true') {
      button.textContent = button.dataset.selectionLabel || '';
      button.disabled = button.dataset.selectionWasDisabled === 'true';
      button.classList.remove('button--selection-pending');
      delete button.dataset.selectionGated;
      delete button.dataset.selectionLabel;
      delete button.dataset.selectionWasDisabled;
    }
  }

  /**
   * A product page carries up to three of these for the same form - the main one, the sticky bar and the quick buy
   * template - and they all have to move together.
   */
  function paintBuyButtons(formId, isPending) {
    var label = strings().selectOptions;

    document.querySelectorAll('buy-buttons[form="' + formId + '"]').forEach(function (container) {
      // Hides the dynamic checkout button. Left alone, Buy now would still purchase the pre-selected variant and
      // defeat the whole point of this.
      container.classList.toggle('buy-buttons--selection-pending', isPending);

      container.querySelectorAll('button[type="submit"]').forEach(function (button) {
        if (button.closest('.shopify-payment-button')) return;
        gateButton(button, isPending, label);
      });
    });
  }

  /**
   * An option with a single value is confirmed on the shopper's behalf - there is no choice to make, and Liquid marks
   * it with data-single-value. Everything else stays outstanding until it is clicked.
   */
  function optionIsPending(set, fieldset) {
    if (fieldset.hasAttribute('data-single-value')) {
      set.add(fieldset.getAttribute('data-option-position'));
      return false;
    }

    return !set.has(fieldset.getAttribute('data-option-position'));
  }

  function pendingCountFor(picker) {
    var set = touchedSet(picker.getAttribute('form-id'));
    var pending = 0;

    picker.querySelectorAll(OPTION_SELECTOR).forEach(function (fieldset) {
      var isPending = optionIsPending(set, fieldset);
      if (isPending) pending++;
      paintOption(fieldset, isPending);
    });

    return pending;
  }

  function apply() {
    // Guard against recursion: this method mutates the DOM, which re-enters through the MutationObserver.
    if (applying) return;
    applying = true;

    try {
      paint();
    } finally {
      applying = false;
    }
  }

  /**
   * The page mutates constantly on its own - chat and rewards widgets, lazy images - and repainting on every one of
   * those would run this hundreds of times a second for nothing. Only a mutation that brought in (or took away) a
   * picker or a set of buy buttons can change what is painted.
   */
  function touchesSelection(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType !== 1) continue;

      if (node.matches && (node.matches(PICKER_SELECTOR) || node.matches('buy-buttons'))) return true;
      if (node.querySelector && node.querySelector(PICKER_SELECTOR + ', buy-buttons')) return true;
    }

    return false;
  }

  function onMutation(records) {
    for (var i = 0; i < records.length; i++) {
      if (touchesSelection(records[i].addedNodes) || touchesSelection(records[i].removedNodes)) {
        apply();
        return;
      }
    }
  }

  function paint() {
    var seen = new Set();

    pickers().forEach(function (picker) {
      var formId = picker.getAttribute('form-id');
      if (!formId) return;

      seen.add(formId);
      paintBuyButtons(formId, pendingCountFor(picker) > 0);
    });

    // A quick buy modal empties itself when it closes; forget its state so reopening starts from scratch.
    Array.prototype.forEach.call(Array.from(touched.keys()), function (formId) {
      if (!seen.has(formId)) touched.delete(formId);
    });
  }

  var schedule = apply;

  // ---------------------------------------------------------------------------------------------------------------
  // Confirming a selection
  // ---------------------------------------------------------------------------------------------------------------

  function contextFor(element) {
    // An open popover is re-parented to <body> by the theme, so it carries its own copy of the option identity
    // rather than relying on the fieldset still being an ancestor.
    var scope = element.closest('[data-option-position][data-form-id]');
    if (!scope) return null;

    return {
      formId: scope.getAttribute('data-form-id'),
      position: scope.getAttribute('data-option-position')
    };
  }

  function labelFor(control) {
    var label = control.getAttribute('data-option-value-label');
    if (label) return label;

    // Keyboard selection reports the input, which does not carry the label itself.
    if (control.id) {
      var associated = document.querySelector('[data-option-value-label][for="' + control.id + '"]');
      if (associated) return associated.getAttribute('data-option-value-label');
    }

    return null;
  }

  function confirmSelection(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var control = target.closest('[data-option-value-label], input[data-option-position]');
    if (!control) return;

    var context = contextFor(control);
    if (!context) return;

    var picker = pickerFor(context.formId);
    if (!picker) return;

    touchedSet(context.formId).add(context.position);

    // Show the chosen value straight away instead of waiting for the server round trip. The attribute is updated too,
    // so the next paint restores what was clicked rather than the value the server last rendered.
    var label = labelFor(control);

    if (label) {
      var fieldset = picker.querySelector(
        '.variant-picker__option[data-option-position="' + context.position + '"]'
      );
      var valueElement = fieldset && valueElementFor(fieldset);

      if (valueElement) valueElement.setAttribute('data-selected-value', label);
    }

    schedule();
  }

  function isPending(formId) {
    var picker = pickerFor(formId);
    if (!picker) return false;

    var set = touchedSet(formId);

    return Array.prototype.some.call(picker.querySelectorAll(OPTION_SELECTOR), function (fieldset) {
      return optionIsPending(set, fieldset);
    });
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------------------------------------------

  document.addEventListener('click', confirmSelection, true);
  document.addEventListener('change', confirmSelection, true);

  // Belt and braces for anything that submits the form without going through the button, such as Enter in a field.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.id || !isPending(form.id)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener('variant:change', schedule);

  function init() {
    if (HONOUR_DEEP_LINK) {
      // A ?variant= link is a deliberate choice - honour it and leave Add to cart alone.
      pickers().forEach(function (picker) {
        var set = touchedSet(picker.getAttribute('form-id'));

        picker.querySelectorAll(OPTION_SELECTOR).forEach(function (fieldset) {
          set.add(fieldset.getAttribute('data-option-position'));
        });
      });
    }

    apply();

    // The picker and buy buttons are replaced wholesale on every option change, and the quick buy modal injects a
    // whole product form at once, so the state is re-applied from the document rather than bound to one element.
    // Deliberately synchronous - this callback is a microtask, so the repaint lands before the browser paints the
    // server's freshly re-rendered (and fully selected) markup.
    new MutationObserver(onMutation).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
